import React, { useEffect, useMemo, useState } from 'react';
import {
    IonModal,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonContent,
    IonFooter,
    IonRow,
    IonCol,
    IonButton,
    IonItem,
    IonLabel,
    IonInput,
    IonList,
    IonIcon,
    useIonAlert,
} from '@ionic/react';
import {pencil, saveOutline, close, trashOutline} from 'ionicons/icons';
import useStore from '../zustand/useStore';
import {
    actionToGetAllNewsCategoriesAdmin,
    actionToCreateNewsCategory,
    actionToUpdateNewsCategory, actionToDeleteNewsCategory,
} from '../apiHelpers/CommonAction';

function slugify(v) {
    return v
        .toLowerCase()
        .trim()
        .replace(/&/g, 'and')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
}

export default function AddNewsCategoryModal({ isOpen, onClose }) {
    const { newsCategory } = useStore(); // using store only to read initially
    const { categoryData,loading = [] } = newsCategory || {};

    // Add-new state
    const [newName, setNewName] = useState('');
    const [newOrdering, setNewOrdering] = useState('');
    const newSlug = useMemo(() => slugify(newName), [newName]);

    // Edit state
    const [editingId, setEditingId] = useState(null);
    const [editName, setEditName] = useState('');
    const [editOrdering, setEditOrdering] = useState('');

    const [presentAlert] = useIonAlert();

    const closeAndReset = () => {
        onClose();
        setNewName('');
        setNewOrdering('');
        setEditingId(null);
        setEditName('');
        setEditOrdering('');
    };

    const fetchCategories = async () => {
        actionToGetAllNewsCategoriesAdmin(false);
    };

    useEffect(() => {
        if (isOpen) fetchCategories();
    }, [isOpen]);

    // Add new category
    const handleAdd = async () => {
        const name = newName.trim();
        const ordering = newOrdering.trim();
        if (!name || !ordering) {
            presentAlert('Please enter both name and ordering.');
            return;
        }
        try {
            await actionToCreateNewsCategory({
                name,
                slug: newSlug, // server can ignore/override if it handles slug
                ordering: Number(ordering),
            });
            setNewName('');
            setNewOrdering('');
            fetchCategories();
        } catch (e) {
            presentAlert('Failed to save category.');
        }
    };

    // Start editing a row
    const startEdit = (cat) => {
        setEditingId(cat.id);
        setEditName(cat.name || '');
        setEditOrdering(
            cat.ordering !== undefined && cat.ordering !== null ? String(cat.ordering) : ''
        );
    };

    // Cancel editing
    const cancelEdit = () => {
        setEditingId(null);
        setEditName('');
        setEditOrdering('');
    };

    // Save edit (button or Enter)
    const saveEdit = async () => {
        const name = editName.trim();
        const ordering = editOrdering.trim();
        if (!name || !ordering) {
            presentAlert('Please enter both name and ordering.');
            return;
        }
        try {
            await actionToUpdateNewsCategory(editingId, {
                name,
                ordering: Number(ordering),
            });
            cancelEdit();
            fetchCategories();
        } catch (e) {
            presentAlert('Failed to update category.');
        }
    };

    const onEditKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            saveEdit();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            cancelEdit();
        }
    };

    const confirmDelete = (cat) => {
        presentAlert({
            header: 'Delete category?',
            message: `"${cat.name}" will be permanently removed.`,
            buttons: [
                { text: 'Cancel', role: 'cancel' },
                {
                    text: 'Delete',
                    role: 'destructive',
                    handler: async () => {
                        try {
                            await actionToDeleteNewsCategory(cat.id);
                            if (editingId === cat.id) cancelEdit();
                            fetchCategories();
                        } catch {
                            presentAlert('Failed to delete category.');
                        }
                    },
                },
            ],
        });
    };

    return (
        <IonModal
            isOpen={isOpen}
            onDidDismiss={closeAndReset}
            initialBreakpoint={1}
            breakpoints={[0, 1]}
            handleBehavior="cycle"
            className={"add_cat_modal_news_admin"}
        >
            <IonHeader className="job_detail_sheet_header sheet_modal_header">
                <IonToolbar>
                    <IonTitle>News Categories</IonTitle>
                </IonToolbar>
            </IonHeader>

            <IonContent className="ion-padding bottom-2 no_padding_top">
                {/* Add new category */}
                <IonItem className="ion-margin-bottom">
                    <IonLabel position="stacked">
                        <h2>Add new category</h2>
                    </IonLabel>

                    <IonInput
                        className="rfp_form_input addcat-input-admin" placeholder="Category name" value={newName}
                        onIonInput={(e) => setNewName(e.detail.value || '')} />

                    <div className="form-row-joint-inputcat">
                        <IonInput
                            className="rfp_form_input addcat-input-admin" style={{ maxWidth: 140 }} type="number" placeholder="Ordering"
                            value={newOrdering} onIonInput={(e) => setNewOrdering(e.detail.value || '')} />

                        <IonButton className="ion-margin-start" onClick={handleAdd} disabled={!newName.trim() || !newOrdering.trim()}>
                            <IonIcon slot="start" icon={saveOutline} />
                            Save
                        </IonButton>
                    </div>
                </IonItem>

                {/* Existing categories */}
                <IonList>
                    {loading ? (
                        <IonItem>Loading…</IonItem>
                    ) : categoryData.length === 0 ? (
                        <IonItem>No categories found.</IonItem>
                    ) : (
                        categoryData.map((cat) => (
                            <IonItem key={cat.id} lines="full">
                                {editingId === cat.id ? (
                                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', width: '100%' }}>
                                        <IonInput
                                            value={editName}
                                            onIonInput={(e) => setEditName(e.detail.value || '')}
                                            onKeyDown={onEditKeyDown}
                                            placeholder="Name"
                                        />
                                        <IonInput
                                            style={{ maxWidth: 120 }}
                                            type="number"
                                            value={editOrdering}
                                            onIonInput={(e) => setEditOrdering(e.detail.value || '')}
                                            onKeyDown={onEditKeyDown}
                                            placeholder="Ordering"
                                        />
                                        <IonButton onClick={saveEdit}>
                                            <IonIcon icon={saveOutline} slot="icon-only" />
                                        </IonButton>
                                        <IonButton color="medium" onClick={cancelEdit}>
                                            <IonIcon icon={close} slot="icon-only" />
                                        </IonButton>
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', width: '100%' }}>
                                        <IonLabel style={{ width: '80%' }}>
                                            <div style={{ fontWeight: 600 }}>{cat.name}</div>
                                            <div style={{ fontSize: 12, opacity: 0.7 }}>
                                                ordering: {cat.ordering ?? '-'}
                                            </div>
                                        </IonLabel>
                                        <IonButton
                                            onClick={() => startEdit(cat)}
                                            slot="end"
                                            fill="clear"
                                            className="ion-margin-start"
                                        >
                                            <IonIcon icon={pencil} />
                                        </IonButton>
                                        <IonButton onClick={() => confirmDelete(cat)} slot="end" fill="clear" color="danger">
                                            <IonIcon icon={trashOutline} />
                                        </IonButton>
                                    </div>
                                )}
                            </IonItem>
                        ))
                    )}
                </IonList>
            </IonContent>

            <IonFooter className="job_detail_sheet_footer ion-padding">
                <IonToolbar>
                    <IonRow>
                        <IonCol>
                            <IonButton className="close_job_detail_button" expand="block" onClick={closeAndReset}>
                                Close
                            </IonButton>
                        </IonCol>
                    </IonRow>
                </IonToolbar>
            </IonFooter>
        </IonModal>
    );
}
